'use client';

import type { Assign } from '@ark-ui/react';
import { Select, createListCollection } from '@ark-ui/react';
import {
  type SelectVariantProps,
  select,
  type ComponentProps,
  type HTMLStyledProps,
  Box,
  HStack,
} from '@terra/ui-styled-system';
import { Tick01Icon, ArrowDown01Icon } from '@terra/ui-icons';
import type { ReactNode } from 'react';
import { forwardRef, useMemo } from 'react';
import { createStyleContext } from './utils/create-style-context';
import { FilterOptionHelpTooltip } from '../filter-option-help-tooltip';

const { withProvider, withContext } = createStyleContext(select);

export type RootProviderProps = ComponentProps<typeof RootProvider>;
export const RootProvider = withProvider<
  HTMLDivElement,
  Assign<
    Assign<
      HTMLStyledProps<'div'>,
      Select.RootProviderProps<Select.CollectionItem>
    >,
    SelectVariantProps
  >
>(Select.RootProvider, 'root');
export type RootProps = ComponentProps<typeof Root>;
export const Root = withProvider<
  HTMLDivElement,
  Assign<
    Assign<HTMLStyledProps<'div'>, Select.RootProps<Select.CollectionItem>>,
    SelectVariantProps
  >
>(Select.Root, 'root');
export const ClearTrigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, Select.ClearTriggerProps>
>(Select.ClearTrigger, 'clearTrigger');
export const Content = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Select.ContentProps>
>(Select.Content, 'content');
export const Control = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Select.ControlProps>
>(Select.Control, 'control');
export const Indicator = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Select.IndicatorProps>
>(Select.Indicator, 'indicator');
export const ItemGroupLabel = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Select.ItemGroupLabelProps>
>(Select.ItemGroupLabel, 'itemGroupLabel');
export const ItemGroup = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Select.ItemGroupProps>
>(Select.ItemGroup, 'itemGroup');
export const ItemIndicator = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Select.ItemIndicatorProps>
>(Select.ItemIndicator, 'itemIndicator');
export const Item = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Select.ItemProps>
>(Select.Item, 'item');
export const ItemText = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'span'>, Select.ItemTextProps>
>(Select.ItemText, 'itemText');
export const Label = withContext<
  HTMLLabelElement,
  Assign<HTMLStyledProps<'label'>, Select.LabelProps>
>(Select.Label, 'label');
export const List = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Select.ListProps>
>(Select.List, 'list');
export const Positioner = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Select.PositionerProps>
>(Select.Positioner, 'positioner');
const BaseTrigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, Select.TriggerProps>
>(Select.Trigger, 'trigger');

export const Trigger = forwardRef<
  HTMLButtonElement,
  ComponentProps<typeof BaseTrigger>
>((props, ref) => <BaseTrigger ref={ref} data-bi-unmask="" {...props} />);
Trigger.displayName = 'Select.Trigger';

export const ValueText = withContext<
  HTMLSpanElement,
  Assign<HTMLStyledProps<'span'>, Select.ValueTextProps>
>(Select.ValueText, 'valueText');
export {
  SelectContext as Context,
  SelectHiddenSelect as HiddenSelect,
} from '@ark-ui/react';
type SelectWithOptionsOption = {
  label: string;
  value: string;
  disabled?: boolean;
  tooltip?: string;
};
type SelectWithOptionsProps = {
  options: SelectWithOptionsOption[];
  value: string[];
  onValueChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  borderRadius?: ComponentProps<typeof Trigger>['borderRadius'];
  variant?: 'default' | 'borderless';
  fitContent?: boolean;
  spaceBetween?: boolean;
  label?: ReactNode;
};

export function SelectWithOptions({
  options,
  value,
  onValueChange,
  placeholder,
  disabled = false,
  borderRadius = 'full',
  variant = 'default',
  fitContent: fitContentProp = false,
  spaceBetween = false,
  label,
}: SelectWithOptionsProps) {
  const hasSelectedValue = value.length > 0 && Boolean(value[0]);
  const collection = useMemo(
    () =>
      createListCollection({
        items: options,
      }),
    [options],
  );
  const borderlessProps =
    variant === 'borderless'
      ? {
          borderWidth: 0,
          borderColor: 'transparent',
          bg: 'transparent',
        }
      : undefined;
  const fitContentStyles = { w: 'fit-content' as const };

  return (
    <Box {...(fitContentProp ? fitContentStyles : {})}>
      <Root
        positioning={{ sameWidth: !fitContentProp }}
        collection={collection}
        value={value}
        onValueChange={(e) => onValueChange(e.value[0])}
        disabled={disabled}
        size="sm"
        {...(fitContentProp
          ? fitContentStyles
          : { minW: '150px', flexShrink: 0 })}
      >
        {label && <Label>{label}</Label>}
        <Control
          {...borderlessProps}
          {...(fitContentProp ? fitContentStyles : {})}
        >
          <Trigger
            h="32px"
            fontWeight="normal"
            borderRadius={borderRadius}
            bg="bg.default"
            color={hasSelectedValue ? 'fg.default' : 'fg.neutral.subtle'}
            _hover={{
              bg: 'bg.neutral.subtleSoft',
              color: hasSelectedValue ? 'fg.default' : 'fg.neutral.muted',
            }}
            display="flex"
            alignItems="center"
            justifyContent={spaceBetween ? 'space-between' : 'flex-start'}
            gap="8px"
            textAlign="left"
            {...(spaceBetween ? { w: 'full' as const } : {})}
            {...(fitContentProp ? fitContentStyles : {})}
            {...borderlessProps}
          >
            <ValueText
              placeholder={placeholder}
              fontWeight="normal"
              color={hasSelectedValue ? 'fg.default' : 'fg.neutral.subtle'}
              {...(spaceBetween ? { flex: 1, minW: 0 } : {})}
            />
            <ArrowDown01Icon size={16} />
          </Trigger>
        </Control>
        <Positioner>
          <Content {...(fitContentProp ? { minW: '160px' } : {})}>
            {options.map((option) => (
              <Item key={option.value} item={option}>
                <HStack gap={2}>
                  <ItemText
                    style={{
                      opacity: option.disabled ? 0.5 : 1,
                    }}
                  >
                    {option.label}
                  </ItemText>
                  {option.tooltip && (
                    <FilterOptionHelpTooltip
                      title={option.tooltip}
                      iconSize={16}
                    />
                  )}
                </HStack>
                <ItemIndicator>
                  <Tick01Icon size={16} />
                </ItemIndicator>
              </Item>
            ))}
          </Content>
        </Positioner>
      </Root>
    </Box>
  );
}
